import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { setTheme } from 'ducks/layout/layoutSlice';
import { Link } from 'react-router-dom';
import { PATHS } from 'routes/routes.constants';

import ContentSwitcher from '@polyai/ui/components/atoms/ContentSwitcher';
import {
  ChevronRightIcon,
  LogoutIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
  UserIcon,
} from '@polyai/ui/components/atoms/Icons';
import Popover, {
  PopoverContent,
  PopoverTrigger,
} from '@polyai/ui/components/atoms/Popover';
import { THEME_NAMES } from '@polyai/ui/styles/config/theme.constants';
import { cn } from '@polyai/ui/utils';

import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import { useUserAccess } from 'hooks/useProjectResources/useUserAccess';
import FadeButton from './FadeButton';
import { userMenuStyles } from './UserMenu.styles';

const truncateEmail = (email?: string) => {
  if (!email) {
    return 'User menu';
  }
  if (email.length < 20) {
    return email;
  }

  return `${email.slice(0, 17)}...`;
};

type UserMenuProps = {
  isExpanded: boolean;
  open?: boolean;
  onPopoverOpenChange?: (open: boolean) => void;
};

const UserMenu = ({ isExpanded, open, onPopoverOpenChange }: UserMenuProps) => {
  const dispatch = useLocalDispatch();
  const logout = useAppSelector(state => state.auth.logout);
  const currentTheme = useAppSelector(state => state.layout.theme);
  const { isInternalUser } = useUserAccess();
  const { user } = useAuth0();
  const { isFeatureOn } = useIsFeatureOn();

  const isDarkModeEnabled = isFeatureOn('dark_mode');

  const handlePopoverOpenChange = useCallback(
    (isOpen: boolean) => {
      onPopoverOpenChange?.(isOpen);
    },
    [onPopoverOpenChange],
  );

  const handleThemeChange = useCallback(
    (theme: THEME_NAMES) => {
      dispatch(setTheme(theme));
    },
    [dispatch],
  );

  const themeOptions = isDarkModeEnabled
    ? [
        {
          value: THEME_NAMES.NEW,
          icon: <SunIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to light theme',
        },
        {
          value: THEME_NAMES.DARK,
          icon: <MoonIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to dark theme',
        },
        {
          value: THEME_NAMES.SYSTEM,
          icon: <MonitorIcon color="iconPrimaryInverse" size="sm" />,
          ariaLabel: 'Switch to system theme',
        },
      ]
    : [];

  return (
    <Popover
      open={open}
      placement="right-end"
      trigger="hover"
      useDismissProps={{ outsidePress: false }}
      onOpenChange={handlePopoverOpenChange}
    >
      <PopoverTrigger asChild>
        <FadeButton
          isExpanded={isExpanded}
          aria-label="User menu"
          data-test-id="user-menu-button"
          endAdornment={ChevronRightIcon}
          label={truncateEmail(user?.email)}
          style={{ padding: '0.625rem' }}
          startAdornment={UserIcon}
          type="button"
        />
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'overflow-clip w-[220px] rounded-medium',
          'border-[color:var(--tw-border-strong-inverse)]',
        )}
        closeOnFocusOut={false}
        initialFocus={-1}
      >
        <Link
          className={userMenuStyles.popoverRow}
          data-test-id="account-button"
          to={PATHS.USER_ACCOUNT}
        >
          <span className={userMenuStyles.rowLabel}>Account</span>
        </Link>
        {isInternalUser && (
          <a className={userMenuStyles.popoverRow} href={PATHS.ADMIN_PAGE}>
            <span className={userMenuStyles.rowLabel}>Admin</span>
            <div className={userMenuStyles.rowDivider} />
          </a>
        )}
        {isDarkModeEnabled && themeOptions.length > 0 && (
          <div className={userMenuStyles.themeRow}>
            <span className={userMenuStyles.rowLabel}>Theme</span>
            <ContentSwitcher
              ariaLabel="Theme picker"
              options={themeOptions}
              size="small"
              value={currentTheme}
              inverse
              onChange={handleThemeChange}
            />
            <div className={userMenuStyles.rowDivider} />
          </div>
        )}
        <button
          className={userMenuStyles.popoverRow}
          data-test-id="logout-btn"
          type="button"
          onClick={() => (logout as () => void)?.()}
        >
          <span className={userMenuStyles.rowLabel}>Log out</span>
          <LogoutIcon color="iconSecondaryInverse" size="sm" />
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default UserMenu;
